import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import { useTranslation } from "common/locales";
import {
  ConfirmCovers,
  EnterCovers,
  EnterReasons,
  EnterTime,
} from "features/bulkCoverInput";
import ConfirmCancellationDialog from "features/general/ConfirmCancellationDialog";
import { StyledInputContainer } from "features/StyledInputContainer";
import { Field, ManualDMInputItem, useFields } from "model";
import { useInvalidateFieldDataQuery } from "model/useFieldQueries";
import { useCreateReadingsBulk } from "model/useManualReadingsQueries";
import { useDeleteTilesForField } from "model/useTileQueries";
import { useCallback, useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useAppStore } from "stores";
import { EventType } from "../../model/manualInputTypes";

function roundToHour(date: Date) {
  const p = 60 * 60 * 1000; // milliseconds in an hour
  return new Date(Math.round(date.getTime() / p) * p);
}

export default function Covers() {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentFarmId = useAppStore((state) => state.currentFarmId);

  // React Query mutations
  const { mutateAsync: createReadingsBulk } = useCreateReadingsBulk();
  const { mutateAsync: deleteTilesForField } = useDeleteTilesForField();
  const invalidateFieldData = useInvalidateFieldDataQuery();

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const handleClose = () => {
    navigate(`/farm/${currentFarmId || ""}`);
  };

  // Use React Query for fields data
  const { data: fields = [] } = useFields();

  const [manualDMInputs, setManualDMInputs] = useState<
    Array<ManualDMInputItem>
  >([]);
  const [dateTimeMeasurementStart, setDateTimeMeasurementStart] = useState(
    roundToHour(new Date())
  );
  const [dateTimeMeasurementEnd, setDateTimeMeasurementEnd] = useState(
    roundToHour(new Date())
  );

  // Check if user has entered any cover data
  const hasChanges = useCallback(() => {
    return manualDMInputs.some(
      (input) => input.newDryMatterValue !== undefined
    );
  }, [manualDMInputs]);

  // Listen for cancel/back events from FABs
  useEffect(() => {
    const handleCancelEvent = () => {
      // On enter-time (first step), check if we need to show dialog
      // For now, just close since no data entered on first step
      handleClose();
    };

    const handleBackEvent = () => {
      // Check if user has entered data
      if (hasChanges()) {
        setConfirmCancelOpen(true);
      } else {
        navigate(-1);
      }
    };

    window.addEventListener("cover-flow-cancel", handleCancelEvent);
    window.addEventListener("cover-flow-back", handleBackEvent);

    return () => {
      window.removeEventListener("cover-flow-cancel", handleCancelEvent);
      window.removeEventListener("cover-flow-back", handleBackEvent);
    };
  }, [hasChanges, navigate, currentFarmId]);

  useEffect(() => {
    if (fields.length > 0 && manualDMInputs.length === 0) {
      setManualDMInputs(
        fields.map((field: Field) => ({
          dryMatterValue: field.dryMatterNow,
          field: field.guid,
          fieldName: field.name,
          event: { eventType: EventType.None },
          kind: "user",
          verificationStatus: "Accepted",
        }))
      );
    }
  }, [fields]);

  async function handleSave() {
    const preparedInputs = manualDMInputs.reduce(
      (acc: Array<ManualDMInputItem>, manualInput) => {
        if (manualInput.newDryMatterValue) {
          acc.push({
            ...manualInput,
            dryMatterValue: manualInput.newDryMatterValue,
          });
        }
        return acc;
      },
      []
    );
    await createReadingsBulk(
      preparedInputs.map((i) => ({
        guid: "new",
        manualDryMatterInput: {
          ...i,
          dateTimeMeasurementStart: dateTimeMeasurementStart.toISOString(),
          dateTimeMeasurementEnd: dateTimeMeasurementEnd.toISOString(),
          event: {
            ...i.event,
            dateTimeCut:
              i.event.dateTimeCut && i.event.dateTimeCut.toISOString(),
            dateTimeIn: i.event.dateTimeIn && i.event.dateTimeIn.toISOString(),
            dateTimeOut:
              i.event.dateTimeOut && i.event.dateTimeOut.toISOString(),
          },
        },
      }))
    );
    manualDMInputs
      .filter((i) => i.newDryMatterValue)
      .forEach(async (i) => {
        // it's important to wait for the tiles to be deleted,
        // as we are setting field.tiles=[] when we are invalidating field data,
        // and we need the field to still have tile ids when deleting the tiles for a field
        await deleteTilesForField(i.field);
        invalidateFieldData(i.field);
      });
    notify(t("addCovers.success"));
    mixpanel.track("CoverAdd successful");
    navigate(`/farm/${currentFarmId || ""}`);
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <Routes>
          <Route
            path="enter-time"
            element={
              <EnterTime
                dateTimeMeasurementStart={dateTimeMeasurementStart}
                setDateTimeMeasurementStart={setDateTimeMeasurementStart}
                dateTimeMeasurementEnd={dateTimeMeasurementEnd}
                setDateTimeMeasurementEnd={setDateTimeMeasurementEnd}
              />
            }
          />
          <Route
            path="enter-covers"
            element={
              <EnterCovers
                manualDMInputs={manualDMInputs}
                setManualDMInputs={setManualDMInputs}
              />
            }
          />
          <Route
            path="enter-reasons"
            element={
              <EnterReasons
                manualDMInputs={manualDMInputs}
                setManualDMInputs={setManualDMInputs}
              />
            }
          />
          <Route
            path="confirm-covers"
            element={
              <ConfirmCovers
                dateTimeMeasurementStart={dateTimeMeasurementStart}
                dateTimeMeasurementEnd={dateTimeMeasurementEnd}
                manualDMInputs={manualDMInputs}
                handleSave={handleSave}
              />
            }
          />
        </Routes>
      </StyledInputContainer>
      <ConfirmCancellationDialog
        dialogOpen={confirmCancelOpen}
        closeDialog={() => setConfirmCancelOpen(false)}
        onConfirmCancel={handleClose}
        onCancelCancel={() => setConfirmCancelOpen(false)}
      />
    </Dialog>
  );
}
